import React, { useState } from "react";
import { useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import { useEffect } from "react";
import { useGetWeatherForecastMapQuery } from "../store/slices/mapsSlice";
import { WeatherCodes } from "../WeatherAlerts/WeatherAlertConstants";
import {
  WeatherForecastClusterType,
  WeatherForecastItem,
  WeatherForecastOverlay,
} from "./MapPopUps";
import { WeatherForecastFiltersType } from "./MapFilters";
import { createRoot } from "react-dom/client";
import { OverlayTrigger, Popover } from "react-bootstrap";
import ArrowIcon from "../../assets/icons/arrow_right-01.svg2";
import {
  useGetClientUnitsQuery,
  useGetWeatherDisplayMapColoursQuery,
} from "../store/slices/settingsSlice";
import {
  unitConversions,
  WeatherAlertConditions,
  WeatherForecastOptions,
} from "../Monitoring/WeatherAlertConstants";

function WeatherIcon({
  conditions,
  alert: _alert,
  date,
  marker,
  map,
  clusteringRadius,
  weatherDisplayMapColours,
  clientUnits,
}: {
  conditions?: string[];
  alert: WeatherForecastClusterType;
  date: string;
  marker?: any;
  map: google.maps.Map;
  clusteringRadius: number;
  weatherDisplayMapColours: {
    condition: string;
    from: string;
    to: string;
    color: string;
  }[];
  clientUnits: {
    [key: string]: string;
  };
}) {
  if (date === "") return;
  if (conditions === undefined || conditions.length === 0) {
    conditions = ["significantWeatherCode"];
  }

  const [show, setShow] = useState(false);
  const [hovered, setHovered] = useState(false);

  const alert = {
    ..._alert,
    Items: _alert.Items.map((item) => ({
      ...item,
      Weather: Object.fromEntries(
        Object.entries(item.Weather).map(([date, conditions]) => [
          date,
          Object.fromEntries(
            Object.entries(conditions).map(([cond, value]) => {
              const condition = WeatherForecastOptions.find(
                (c) => c.value === cond
              );
              const unit = condition?.unit
                ? unitConversions[
                    condition.unit as keyof typeof unitConversions
                  ]
                : undefined;

              const convert =
                unit?.conversions.find(
                  (c: any) => c.to === clientUnits[unit?.type || ""]
                )?.convert || ((x: any) => x);

              return [cond, convert?.(value) || value, ,];
            })
          ),
        ])
      ),
    })),
  };

  return (
    <div
      onMouseEnter={() => {
        marker.zIndex = 1000;
        setHovered(true);
      }}
      onMouseLeave={() => {
        marker.zIndex = undefined;
        setShow(false);
        setHovered(false);
      }}
      onClick={() => setShow(true)}
    >
      <OverlayTrigger
        show={show}
        placement="auto"
        overlay={
          <Popover className="bg-purple-gradient mx-n2">
            <Popover.Header as="h3">Weather forecast details</Popover.Header>
            <Popover.Body>
              <WeatherForecastOverlay
                data={alert}
                date={date}
                clientUnits={clientUnits}
              />
            </Popover.Body>
          </Popover>
        }
      >
        <div
          className={
            "d-flex flex-column position-relative justify-content-center p-1 " +
            (hovered ? "bg-secondary bg-opacity-75 rounded-3" : "")
          }
          style={{
            gap: hovered ? "30px" : "0px",
            marginBottom: hovered ? conditions.length * -25 + 25 : "0px",
            transition: "gap 0.3s, margin 0.3s",
          }}
        >
          {conditions &&
            conditions.length > 0 &&
            [...conditions].reverse().map((cond, cond_index) => {
              const condition = WeatherForecastOptions.find(
                (c) => c.value === cond
              );
              const unit = condition?.unit
                ? unitConversions[
                    condition.unit as keyof typeof unitConversions
                  ]
                : undefined;

              let AlertIcon = condition?.icon;

              if (cond === "significantWeatherCode") {
                AlertIcon =
                  WeatherCodes[alert.Items[0].Weather[date][cond]]?.icon;
              }

              if (cond === "wind") {
                AlertIcon = ArrowIcon;
              }

              let values = [
                {
                  condition: cond,
                  value: alert.IsCluster
                    ? Math.round(
                        Math.min(
                          ...alert.Items.map(
                            (item: any) => item.Weather[date][cond] || 0
                          )
                        )
                      ) ===
                      Math.round(
                        Math.round(
                          Math.max(
                            ...alert.Items.map(
                              (item: any) => item.Weather[date][cond] || 0
                            )
                          )
                        )
                      )
                      ? Math.round(
                          Math.max(
                            ...alert.Items.map(
                              (item: any) => item.Weather[date][cond] || 0
                            )
                          )
                        )
                      : `${Math.round(
                          Math.min(
                            ...alert.Items.map(
                              (item: any) => item.Weather[date][cond] || 0
                            )
                          )
                        )} - ${Math.round(
                          Math.round(
                            Math.max(
                              ...alert.Items.map(
                                (item: any) => item.Weather[date][cond] || 0
                              )
                            )
                          )
                        )}`
                    : Math.round(
                        (alert as any).Items[0].Weather[date][cond] || 0
                      ),
                },
              ];
              if (cond === "wind") {
                values = [
                  {
                    condition: "windDirectionFrom10m",
                    value: alert.IsCluster
                      ? Math.round(
                          Math.min(
                            ...alert.Items.map(
                              (item: any) =>
                                item.Weather[date].windDirectionFrom10m || 0
                            )
                          )
                        ) ===
                        Math.round(
                          Math.max(
                            ...alert.Items.map(
                              (item: any) =>
                                item.Weather[date].windDirectionFrom10m || 0
                            )
                          )
                        )
                        ? Math.round(
                            Math.max(
                              ...alert.Items.map(
                                (item: any) =>
                                  item.Weather[date].windDirectionFrom10m || 0
                              )
                            )
                          )
                        : `${Math.round(
                            Math.min(
                              ...alert.Items.map(
                                (item: any) =>
                                  item.Weather[date].windDirectionFrom10m || 0
                              )
                            )
                          )} - ${Math.round(
                            Math.max(
                              ...alert.Items.map(
                                (item: any) =>
                                  item.Weather[date].windDirectionFrom10m || 0
                              )
                            )
                          )}`
                      : Math.round(
                          alert.Items[0].Weather[date].windDirectionFrom10m || 0
                        ),
                  },
                  {
                    condition: "windSpeed10m",
                    value: alert.IsCluster
                      ? Math.round(
                          Math.min(
                            ...alert.Items.map(
                              (item: any) =>
                                item.Weather[date].windSpeed10m || 0
                            )
                          )
                        ) ===
                        Math.round(
                          Math.max(
                            ...alert.Items.map(
                              (item: any) =>
                                item.Weather[date].windSpeed10m || 0
                            )
                          )
                        )
                        ? Math.round(
                            Math.max(
                              ...alert.Items.map(
                                (item: any) =>
                                  item.Weather[date].windSpeed10m || 0
                              )
                            )
                          )
                        : `${Math.round(
                            Math.min(
                              ...alert.Items.map(
                                (item: any) =>
                                  item.Weather[date].windSpeed10m || 0
                              )
                            )
                          )} - ${Math.round(
                            Math.max(
                              ...alert.Items.map(
                                (item: any) =>
                                  item.Weather[date].windSpeed10m || 0
                              )
                            )
                          )}`
                      : Math.round(
                          alert.Items[0].Weather[date].windSpeed10m || 0
                        ),
                  },
                ];
              } else if (cond === "significantWeatherCode") {
                values = [
                  {
                    condition: "significantWeatherCode",
                    value: alert.IsCluster
                      ? WeatherCodes[
                          Math.min(
                            ...alert.Items.map(
                              (item: any) =>
                                item.Weather[date].significantWeatherCode || 0
                            )
                          )
                        ]?.label ===
                        WeatherCodes[
                          Math.max(
                            ...alert.Items.map(
                              (item: any) =>
                                item.Weather[date].significantWeatherCode || 0
                            )
                          )
                        ]?.label
                        ? WeatherCodes[
                            Math.max(
                              ...alert.Items.map(
                                (item: any) =>
                                  item.Weather[date].significantWeatherCode || 0
                              )
                            )
                          ]?.label
                        : `${
                            WeatherCodes[
                              Math.min(
                                ...alert.Items.map(
                                  (item: any) =>
                                    item.Weather[date].significantWeatherCode ||
                                    0
                                )
                              )
                            ]?.label
                          } - ${
                            WeatherCodes[
                              Math.max(
                                ...alert.Items.map(
                                  (item: any) =>
                                    item.Weather[date].significantWeatherCode ||
                                    0
                                )
                              )
                            ]?.label
                          }`
                      : WeatherCodes[alert.Items[0].Weather[date][cond] || 0]
                          ?.label,
                  },
                ];
              }

              const bgColor =
                weatherDisplayMapColours.find((wc) => {
                  if (cond === "significantWeatherCode") {
                    return (
                      wc.condition === values[0].condition &&
                      (!wc.from ||
                        wc.from <=
                          (alert.Items[0].Weather[date][cond] || "0")) &&
                      (!wc.to ||
                        wc.to >= (alert.Items[0].Weather[date][cond] || "0"))
                    );
                  }
                  if (cond === "wind") {
                    return (
                      wc.condition === "windSpeed10m" &&
                      (!wc.from ||
                        wc.from <=
                          (alert.Items[0].Weather[date]["windSpeed10m"] ||
                            "0")) &&
                      (!wc.to ||
                        wc.to >=
                          (alert.Items[0].Weather[date]["windSpeed10m"] || "0"))
                    );
                  }
                  return (
                    wc.condition === values[0].condition &&
                    (!wc.from || wc.from < values[0].value) &&
                    (!wc.to || wc.to >= values[0].value)
                  );
                })?.color || "rgba(10, 10, 10, 0.8)";

              // Each icon
              return (
                <div
                  className="d-flex align-items-start"
                  style={{
                    marginBottom:
                      cond_index + 1 === conditions.length ? 0 : -20,
                  }}
                >
                  <div
                    className="p-1 rounded-circle text-white border border-white"
                    style={{
                      width: 25,
                      height: 25,
                      backgroundColor: bgColor,
                    }}
                  >
                    {AlertIcon && (
                      <AlertIcon
                        className="white icon-bold"
                        style={{
                          ...(cond === "wind"
                            ? {
                                transform: `rotate(${
                                  (values.find(
                                    (v) =>
                                      v.condition === "windDirectionFrom10m" &&
                                      typeof v.value === "number"
                                  )?.value as number) - 90
                                }deg)`,
                                color: "red",
                              }
                            : {}),
                        }}
                      />
                    )}
                  </div>
                  {hovered && (
                    <div className="">
                      {values
                        .filter(
                          (v) => !["windDirectionFrom10m"].includes(v.condition)
                        )
                        .map((value) => {
                          const bgColor =
                            weatherDisplayMapColours.find((wc) => {
                              if (cond === "significantWeatherCode") {
                                return (
                                  wc.condition === values[0].condition &&
                                  (!wc.from ||
                                    wc.from <=
                                      (alert.Items[0].Weather[date][cond] ||
                                        "0")) &&
                                  (!wc.to ||
                                    wc.to >=
                                      (alert.Items[0].Weather[date][cond] ||
                                        "0"))
                                );
                              }
                              if (cond === "wind") {
                                return (
                                  wc.condition === "windSpeed10m" &&
                                  (!wc.from ||
                                    wc.from <=
                                      (alert.Items[0].Weather[date][
                                        "windSpeed10m"
                                      ] || "0")) &&
                                  (!wc.to ||
                                    wc.to >=
                                      (alert.Items[0].Weather[date][
                                        "windSpeed10m"
                                      ] || "0"))
                                );
                              }
                              return (
                                wc.condition === values[0].condition &&
                                (!wc.from || wc.from < values[0].value) &&
                                (!wc.to || wc.to >= values[0].value)
                              );
                            })?.color || "rgba(10, 10, 10, 0.8)";
                          return (
                            <span
                              className="rounded-pill text-white px-2"
                              style={{
                                backgroundColor: bgColor,
                              }}
                              title={
                                WeatherForecastOptions.find(
                                  (wac) => value.condition === wac.value
                                )?.label
                              }
                            >
                              {value.value} {clientUnits[unit?.type || ""]}
                            </span>
                          );
                        })}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      </OverlayTrigger>
    </div>
  );
}

export default function WeatherForecasts({
  setOverlay,
  filters,
  setLoading,
  setWeatherForecastDates,
}: {
  setOverlay: any;
  filters?: WeatherForecastFiltersType;
  setLoading?: (isLoading: boolean) => void;
  setWeatherForecastDates?: (dates: string[]) => void;
}) {
  const weatherDisplayMapColours =
    useGetWeatherDisplayMapColoursQuery()?.data?.colours;

  const clientUnits = useGetClientUnitsQuery()?.data;

  const map = useMap();
  const marker = useMapsLibrary("marker");
  const [mapBounds, setMapBounds] = useState<{
    west: number;
    north: number;
    east: number;
    south: number;
    centerLng: number;
    centerLat: number;
  }>();
  const [searchArea, setSearchArea] = useState<any[][]>();

  const mapGeometry = useMapsLibrary("geometry");
  const clusteringRadius =
    (map?.getZoom() as number) > 9
      ? 0
      : (map?.getZoom() as number) > 5
      ? 40
      : 60;

  const {
    data: { data: clusters, dates: weatherForecastDates } = {
      data: [],
      dates: [],
    },
    isLoading,
    isFetching,
  } = useGetWeatherForecastMapQuery(
    {
      ...(mapBounds || {
        west: 0,
        north: 0,
        east: 0,
        south: 0,
        centerLng: 0,
        centerLat: 0,
      }),
      start: filters?.start,
      radius: clusteringRadius,
      steps: 8,
    },
    { skip: !mapBounds }
  );

  useEffect(() => {
    if (!setLoading) return;

    if (isFetching || isLoading) {
      setLoading(true);
    } else {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  }, [isFetching, isLoading, setLoading]);

  function showClusters({
    clusters,
    map,
    marker,
    weatherDisplayMapColours,
    clientUnits,
  }: {
    clusters: WeatherForecastClusterType[] | undefined;
    map: google.maps.Map;
    marker: google.maps.MarkerLibrary | null;
    weatherDisplayMapColours: any[];
    clientUnits: {
      [key: string]: string;
    };
  }) {
    if (!map || !marker) return;
    const locationShapes: any[] = [];

    clusters?.forEach((alert) => {
      const markerElement = document.createElement("div");
      const root = createRoot(markerElement);
      const m = new marker.AdvancedMarkerElement({
        position: {
          lat: alert.Y ?? 0,
          lng: alert.X ?? 0,
        },
        map,
      });

      root.render(
        <WeatherIcon
          conditions={filters?.conditions}
          alert={alert}
          date={filters?.date ?? ""}
          marker={m}
          map={map}
          clusteringRadius={clusteringRadius}
          weatherDisplayMapColours={weatherDisplayMapColours}
          clientUnits={clientUnits}
        />
      );
      m.content = markerElement;
      // if (alert.IsCluster) {
      //   m.addListener("click", (e: any) => {
      //     let bounds = new google.maps.LatLngBounds();
      //     var east = google.maps.geometry.spherical.computeOffset(
      //       {
      //         lat: alert.Y ?? 0,
      //         lng: alert.X ?? 0,
      //       },
      //       clusteringRadius * 1000,
      //       90
      //     );
      //     bounds.extend(east);
      //     var west = google.maps.geometry.spherical.computeOffset(
      //       {
      //         lat: alert.Y ?? 0,
      //         lng: alert.X ?? 0,
      //       },
      //       clusteringRadius * 1000,
      //       270
      //     );
      //     bounds.extend(west);

      //     map.fitBounds(bounds);
      //   });
      // }
      locationShapes.push(m);
    });
    return locationShapes;
  }

  useEffect(() => {
    if (!map || !clusters) return;

    const shapes: any[] =
      showClusters({
        clusters,
        map,
        marker,
        weatherDisplayMapColours,
        clientUnits,
      }) || [];

    setWeatherForecastDates?.(weatherForecastDates);

    return () => {
      shapes.forEach((shape) => {
        shape.setMap?.(null);
        shape.map = null;
      });
    };
  }, [map, clusters, filters, weatherDisplayMapColours, clientUnits]);

  useEffect(() => {
    if (!map) return;

    const boundChageEvent = map.addListener("idle", () => {
      if (!map || !map.getBounds() || !map.getCenter()) return;
      const zoom = map.getZoom();

      setMapBounds({
        centerLng: map.getCenter()?.toJSON().lng || 0,
        centerLat: map.getCenter()?.toJSON().lat || 0,
        ...(map.getBounds()?.toJSON() || {
          west: 0,
          north: 0,
          east: 0,
          south: 0,
        }),
        // zoom: !!zoom && zoom < 7 ? zoom - 1 : undefined,
      });
    });

    return () => {
      google.maps.event.removeListener(boundChageEvent);
    };
  }, [map]);

  const conditions = !!filters?.conditions?.length
    ? filters.conditions
    : ["significantWeatherCode"];

  return (
    <>
      {filters?.showSlider && conditions && filters?.date && (
        <div
          className="z-3 text-white mt-2 pt-1"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.8)" }}
        >
          <marquee behavior="" direction="">
            {clusters
              ?.map((alert: WeatherForecastClusterType) => alert.Items)
              .flat()
              .map((item: WeatherForecastItem, itemIndex: number) => (
                <span key={itemIndex} className="px-3 d-inline-block">
                  <div className="d-flex gap-2 align-items-center">
                    <div className="fw-bold">{item.LocationName}</div>
                    {conditions.map((cond, cond_index) => {
                      let AlertIcon = WeatherForecastOptions.find(
                        (wac) => cond === wac.value
                      )?.icon;

                      if (cond === "significantWeatherCode") {
                        AlertIcon =
                          WeatherCodes[item.Weather[filters?.date][cond]]?.icon;
                      }

                      if (cond === "wind") {
                        AlertIcon = ArrowIcon;
                      }

                      let values = [
                        {
                          condition: cond,
                          value: Math.round(
                            (item as any).Weather[filters?.date][cond] || 0
                          ),
                        },
                      ];
                      if (cond === "wind") {
                        values = [
                          {
                            condition: "windDirectionFrom10m",
                            value: Math.round(
                              item.Weather[filters?.date]
                                .windDirectionFrom10m || 0
                            ),
                          },
                          {
                            condition: "windSpeed10m",
                            value: Math.round(
                              item.Weather[filters?.date].windSpeed10m || 0
                            ),
                          },
                        ];
                      } else if (cond === "significantWeatherCode") {
                        values = [
                          {
                            condition: "significantWeatherCode",
                            value: WeatherCodes[
                              item.Weather[filters?.date][cond] || 0
                            ]?.label as any,
                          },
                        ];
                      }
                      // Each icon
                      return (
                        <div className="d-flex align-items-center">
                          <div
                            className="rounded-circle text-white border border-white"
                            style={{
                              width: 24,
                              height: 24,
                              backgroundColor: "#111",
                            }}
                          >
                            {AlertIcon && (
                              <AlertIcon
                                className="white icon-bold"
                                style={{
                                  ...(cond === "wind"
                                    ? {
                                        transform: `rotate(${
                                          (values.find(
                                            (v) =>
                                              v.condition ===
                                                "windDirectionFrom10m" &&
                                              typeof v.value === "number"
                                          )?.value as number) - 90
                                        }deg)`,
                                      }
                                    : {}),
                                }}
                              />
                            )}
                          </div>
                          <div className="">
                            {values
                              .filter(
                                (v) =>
                                  !["windDirectionFrom10m"].includes(
                                    v.condition
                                  )
                              )
                              .map((value) => {
                                const bgColor = [10, 10, 10];
                                if (
                                  [
                                    "feelsLikeTemp",
                                    "minScreenAirTemp",
                                    "maxScreenAirTemp",
                                  ].includes(value.condition) &&
                                  typeof value.value === "number"
                                ) {
                                  bgColor[0] =
                                    (1 - Math.abs(value.value - 40) / 20) * 255; //red
                                  bgColor[1] =
                                    (1 - Math.abs(value.value - 20) / 20) * 255; // green
                                  bgColor[2] =
                                    (1 - Math.abs(value.value) / 20) * 255; // blue
                                }
                                return (
                                  <span
                                    className="rounded-pill text-white px-2"
                                    style={{
                                      backgroundColor: `rgba(${bgColor.join(
                                        ","
                                      )}, 0.8)`,
                                    }}
                                    title={
                                      WeatherForecastOptions.find(
                                        (wac) => value.condition === wac.value
                                      )?.label
                                    }
                                  >
                                    {value.value}{" "}
                                    {
                                      WeatherForecastOptions.find(
                                        (wac) => value.condition === wac.value
                                      )?.unit
                                    }
                                  </span>
                                );
                              })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </span>
              ))}
          </marquee>
        </div>
      )}
    </>
  );
}
